import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Mail, Send, User, FileText, Plus, Trash2, Loader2, 
  CheckCircle, AlertCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import MobileNav from "../components/MobileNav";
import EmailAutomationToggles from "../components/EmailAutomationToggles";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CreditRepairStepsPage() {
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  
  // Single accounts textarea - auto-fills all 3 bureaus
  const [allAccounts, setAllAccounts] = useState("");
  
  // Parse accounts from textarea (one per line)
  const parseAccounts = (text) => {
    return text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
  };
  
  const handleSend = async () => {
    if (!clientName.trim()) {
      toast.error("Please enter client name");
      return;
    }
    if (!clientEmail.trim()) {
      toast.error("Please enter client email");
      return;
    }
    if (!clientEmail.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    const accounts = parseAccounts(allAccounts);
    // accounts can be empty — backend will insert a placeholder line under each bureau
    // ("Name account that were listed in the quote that we are disputing...")
    // so reps can still send the steps when they don't have the credit report in front of them.

    setSending(true);
    
    try {
      // Same accounts go to all 3 bureaus
      const response = await axios.post(`${API}/api/ftc-report/send-credit-repair-steps`, {
        client_name: clientName.trim(),
        client_email: clientEmail.trim(),
        accounts_by_bureau: {
          transunion: accounts,
          equifax: accounts,
          experian: accounts
        }
      });
      
      if (response.data?.success) {
        toast.success(`Credit Repair Steps sent to ${clientEmail}!`);
        setSent(true);
      } else {
        throw new Error(response.data?.error || "Failed to send");
      }
    } catch (error) {
      console.error("Send error:", error);
      toast.error(`Failed to send: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSending(false);
    }
  };
  
  const handleReset = () => {
    setClientName("");
    setClientEmail("");
    setAllAccounts("");
    setSent(false);
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <MobileNav />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-lg">
              <Mail className="w-6 h-6" />
            </div>
            Credit Repair Steps
          </h1>
          <p className="text-zinc-400 mt-1">Send credit repair instructions to clients without creating an FTC report</p>
        </div>
      </div>

      {/* ── Email Automations (Fri mass balance, Step 3, weekly admin digest) ── */}
      <EmailAutomationToggles category="quick_reminders" />
      
      {sent ? (
        /* Success State */
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-green-400 mb-2">Email Sent Successfully!</h2>
            <p className="text-zinc-400 mb-6">
              Credit Repair Steps have been sent to <span className="text-white font-medium">{clientEmail}</span>
            </p>
            <Button onClick={handleReset} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              Send Another
            </Button>
          </CardContent>
        </Card>
      ) : (
        /* Form */
        <div className="space-y-6">
          {/* Client Info Card */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-blue-400" />
                Client Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">Client Name *</Label>
                  <Input
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="John Smith"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                    data-testid="client-name-input"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Client Email *</Label>
                  <Input
                    type="email"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    placeholder="client@example.com"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                    data-testid="client-email-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Accounts Card */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-orange-400" />
                Accounts to Dispute <span className="text-xs font-normal text-zinc-500 ml-2">(optional)</span>
              </CardTitle>
              <p className="text-sm text-zinc-500">
                Enter one account per line. These accounts will automatically appear under ALL 3 bureaus (TransUnion, Equifax, Experian) in Step 3 of the email.
                <br/>
                <span className="text-amber-300/90">
                  Don't have the credit report in front of you?  Leave this blank — the email will tell the client to write in the accounts from the quote we're disputing.
                </span>
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Single Accounts Textarea */}
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-red-400">🔴 TransUnion</span>
                  <span className="text-blue-400">🔵 Equifax</span>
                  <span className="text-green-400">🟢 Experian</span>
                  <span className="text-zinc-500 text-sm ml-2">← All 3 bureaus will receive these accounts</span>
                </div>
                <Textarea
                  value={allAccounts}
                  onChange={(e) => setAllAccounts(e.target.value)}
                  placeholder="CAPITAL ONE AUTO LOAN - Account #1234
AMERICAN EXPRESS - Card ending 5678
LVNV FUNDING LLC - Collection $1,500
PORTFOLIO RECOVERY - Medical Bill $850
MIDLAND CREDIT - Original Creditor: Chase"
                  className="bg-zinc-800 border-zinc-700 min-h-[200px] font-mono text-sm"
                  data-testid="all-accounts-input"
                />
                <div className="flex items-center justify-between mt-2">
                  <p className="text-sm text-zinc-400">
                    {parseAccounts(allAccounts).length > 0 ? (
                      <><span className="text-white font-medium">{parseAccounts(allAccounts).length}</span> account(s) will be sent to all 3 bureaus</>
                    ) : (
                      <span className="text-amber-300/80 italic">No accounts entered — email will use the "Name account..." placeholder</span>
                    )}
                  </p>
                  {allAccounts && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setAllAccounts("")}
                      className="text-xs text-zinc-400 hover:text-red-400"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Preview of how it will appear */}
              {parseAccounts(allAccounts).length === 0 ? (
                <div className="bg-zinc-800/50 rounded-lg p-4 mt-4 border border-amber-500/20">
                  <p className="text-xs text-zinc-500 mb-3 uppercase tracking-wide">Preview - how Step 3 will look in the email (no accounts entered):</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { label: "🔴 TransUnion", color: "red" },
                      { label: "🔵 Equifax", color: "blue" },
                      { label: "🟢 Experian", color: "green" },
                    ].map((b) => (
                      <div key={b.label} className={`bg-${b.color}-500/10 border border-${b.color}-500/20 rounded p-3`}>
                        <p className={`text-${b.color}-400 font-medium text-sm mb-2`}>{b.label}</p>
                        <p className="text-xs text-amber-300/90 italic leading-relaxed">
                          • Name account that were listed in the quote that we are disputing and you need removed
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-zinc-800/50 rounded-lg p-4 mt-4">
                  <p className="text-xs text-zinc-500 mb-3 uppercase tracking-wide">Preview - How accounts will appear in email:</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-red-500/10 border border-red-500/20 rounded p-3">
                      <p className="text-red-400 font-medium text-sm mb-2">🔴 TransUnion</p>
                      <ul className="text-xs text-zinc-400 space-y-1">
                        {parseAccounts(allAccounts).slice(0, 3).map((acc, i) => (
                          <li key={i}>• {acc}</li>
                        ))}
                        {parseAccounts(allAccounts).length > 3 && (
                          <li className="text-zinc-500">...and {parseAccounts(allAccounts).length - 3} more</li>
                        )}
                      </ul>
                    </div>
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded p-3">
                      <p className="text-blue-400 font-medium text-sm mb-2">🔵 Equifax</p>
                      <ul className="text-xs text-zinc-400 space-y-1">
                        {parseAccounts(allAccounts).slice(0, 3).map((acc, i) => (
                          <li key={i}>• {acc}</li>
                        ))}
                        {parseAccounts(allAccounts).length > 3 && (
                          <li className="text-zinc-500">...and {parseAccounts(allAccounts).length - 3} more</li>
                        )}
                      </ul>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/20 rounded p-3">
                      <p className="text-green-400 font-medium text-sm mb-2">🟢 Experian</p>
                      <ul className="text-xs text-zinc-400 space-y-1">
                        {parseAccounts(allAccounts).slice(0, 3).map((acc, i) => (
                          <li key={i}>• {acc}</li>
                        ))}
                        {parseAccounts(allAccounts).length > 3 && (
                          <li className="text-zinc-500">...and {parseAccounts(allAccounts).length - 3} more</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Preview & Send */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="py-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-center md:text-left">
                  <p className="text-zinc-400 text-sm">
                    Ready to send Credit Repair Steps email to:
                  </p>
                  <p className="text-white font-medium">
                    {clientName || "Client Name"} ({clientEmail || "email@example.com"})
                  </p>
                  <p className="text-zinc-500 text-xs mt-1">
                    {parseAccounts(allAccounts).length > 0
                      ? `${parseAccounts(allAccounts).length} account(s) will be sent to all 3 bureaus`
                      : "Email will include the placeholder: \"Name account that were listed in the quote that we are disputing and you need removed\""}
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={handleReset}
                    className="border-zinc-700 text-zinc-400"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear All
                  </Button>
                  <Button
                    onClick={handleSend}
                    disabled={sending || !clientName || !clientEmail}
                    className="bg-blue-600 hover:bg-blue-700 min-w-[150px]"
                    data-testid="send-credit-repair-btn"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Email
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Info Box */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-blue-400 font-medium mb-1">What the client will receive:</p>
                <ul className="text-zinc-400 space-y-1">
                  <li>• <strong>Step 1:</strong> Remove wrong personal info (with word-for-word scripts)</li>
                  <li>• <strong>Step 2:</strong> Freeze 5 secondary bureaus (LexisNexis, SageStream, etc.)</li>
                  <li>• <strong>Step 3:</strong> Call every Friday + their specific accounts listed</li>
                  <li>• <strong>Bonus:</strong> Credit boosting resources (MeetAva, Kikoff, etc.)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
